/**
 * Synchronized selection between the vulnerable and patched replay rails
 * (EL-062). Local UI state only -- selecting a node never mutates evidence,
 * matching architecture section 12's "UI state" note.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import type { ReplaySide } from "@exploitlens/ui-contracts";

export interface Selection {
  side: ReplaySide;
  nodeId: string;
  /** The aligned node's id on the other side, when the aligner found one. */
  counterpartId?: string;
}

interface SelectionContextValue {
  selection: Selection | undefined;
  select: (selection: Selection | undefined) => void;
}

const SelectionContext = createContext<SelectionContextValue | undefined>(undefined);

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selection, select] = useState<Selection | undefined>(undefined);
  const value = useMemo(() => ({ selection, select }), [selection]);
  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection(): SelectionContextValue {
  const value = useContext(SelectionContext);
  if (value === undefined) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return value;
}
